<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { NButton, NEmpty, useMessage } from 'naive-ui'
import type { MaterialGoods, MaterialResource } from '@/material/web/api'
import { getMaterialResourceUrl } from '@/material/web/api'
import { applyMaterialResourceToWorkspace } from '@/features/material-library/apply'
import { areMaterialResourcesAlreadyApplied, resolveMaterialUsageRoot } from '@/features/material-library/usage'
import {
  formatMaterialMusicDuration,
  getMusicResources,
  getMusicResourceDisplayName,
  readMaterialMusicDurationSeconds,
} from '@/material/web/previewUtils'
import { playHtmlAudio, stopHtmlAudio } from '@/features/audio-playback/safariAudioPlayback'
import { useWorkspaceStore } from '@/store/workspace'

const props = defineProps<{
  goods: MaterialGoods
  resources: MaterialResource[]
  topCategoryId: string
  collected: boolean
  previewLoading?: boolean
}>()

const emit = defineEmits<{
  toggleCollect: []
}>()

const audio = new Audio()
const selectedAudioId = ref('')
const playingAudioId = ref('')
const isPlaying = ref(false)
const playbackCurrentTime = ref(0)
const playbackDuration = ref(0)
const durationByResourceId = ref<Record<string, number>>({})
const applying = ref(false)
const message = useMessage()
const workspace = useWorkspaceStore()

const musicResources = computed(() => getMusicResources(props.resources))
const currentAudio = computed(
  () => musicResources.value.find((item) => item.id === selectedAudioId.value) ?? musicResources.value[0] ?? null,
)
const isPlayingCurrent = computed(
  () => !!playingAudioId.value && playingAudioId.value === currentAudio.value?.id && isPlaying.value,
)
const currentAudioDuration = computed(() => resolveResourceDurationSeconds(currentAudio.value))
const playbackTimeLabel = computed(() => {
  if (!currentAudio.value) return ''
  if (isPlayingCurrent.value) {
    return `${formatMaterialMusicDuration(playbackCurrentTime.value)} / ${formatMaterialMusicDuration(currentAudioDuration.value)}`
  }
  return formatMaterialMusicDuration(currentAudioDuration.value)
})
const alreadyApplied = computed(() => {
  const root = resolveMaterialUsageRoot(props.topCategoryId, workspace.virtualResourceRoots)
  return areMaterialResourcesAlreadyApplied(root, currentAudio.value ? [currentAudio.value] : [])
})

/** 曲目时长：优先播放中解码结果，其次接口 music_time 与本地缓存。 */
function resolveResourceDurationSeconds(resource: MaterialResource | null): number {
  if (!resource) return 0
  if (playingAudioId.value === resource.id && playbackDuration.value > 0) {
    return playbackDuration.value
  }
  const cached = durationByResourceId.value[resource.id]
  if (cached && cached > 0) return cached
  return readMaterialMusicDurationSeconds(resource)
}

function cacheResourceDuration(resourceId: string, seconds: number): void {
  if (!resourceId || !Number.isFinite(seconds) || seconds <= 0) return
  durationByResourceId.value = {
    ...durationByResourceId.value,
    [resourceId]: Math.floor(seconds),
  }
}

function resetPlaybackProgress(): void {
  playbackCurrentTime.value = 0
  playbackDuration.value = 0
}

function syncPlaybackDurationFromAudio(): void {
  if (!Number.isFinite(audio.duration) || audio.duration <= 0) return
  playbackDuration.value = Math.floor(audio.duration)
  if (playingAudioId.value) {
    cacheResourceDuration(playingAudioId.value, playbackDuration.value)
  }
}

watch(
  () => props.goods.id,
  () => {
    durationByResourceId.value = {}
    resetPlaybackProgress()
  },
)

watch(
  musicResources,
  (list) => {
    selectedAudioId.value = list[0]?.id ?? ''
    stopHtmlAudio(audio)
    playingAudioId.value = ''
    isPlaying.value = false
    resetPlaybackProgress()
  },
  { immediate: true },
)

audio.onplay = () => {
  isPlaying.value = true
}

audio.onpause = () => {
  isPlaying.value = false
}

audio.onloadedmetadata = () => {
  syncPlaybackDurationFromAudio()
}

audio.ontimeupdate = () => {
  playbackCurrentTime.value = Math.floor(audio.currentTime)
  syncPlaybackDurationFromAudio()
}

function togglePlay(item: MaterialResource) {
  const nextUrl = getMaterialResourceUrl(item.resmd5)
  if (playingAudioId.value === item.id) {
    if (audio.paused) {
      void audio.play()
    } else {
      stopHtmlAudio(audio)
      playingAudioId.value = ''
      resetPlaybackProgress()
    }
    return
  }
  selectedAudioId.value = item.id
  playingAudioId.value = item.id
  resetPlaybackProgress()
  void playHtmlAudio(audio, nextUrl, {
    mimeHint: 'audio/mpeg',
    onEnded: () => {
      playingAudioId.value = ''
      isPlaying.value = false
      resetPlaybackProgress()
    },
  }).catch(() => {
    playingAudioId.value = ''
    isPlaying.value = false
    resetPlaybackProgress()
  })
}

async function applyCurrentAudio() {
  if (!currentAudio.value) {
    message.warning('当前没有可应用的音频资源')
    return
  }
  applying.value = true
  try {
    await applyMaterialResourceToWorkspace({
      topCategoryId: props.topCategoryId,
      goods: props.goods,
      resource: currentAudio.value,
    })
  } catch (error) {
    message.error(error instanceof Error ? error.message : '音频应用失败')
  } finally {
    applying.value = false
  }
}

onBeforeUnmount(() => {
  stopHtmlAudio(audio)
})
</script>

<template>
  <div class="preview-pane">
    <header class="preview-header">
      <div class="preview-title">{{ goods.name }}</div>
      <div class="preview-subtitle">{{ goods.extra || goods.intro || '暂无说明' }}</div>
      <div v-if="currentAudio" class="music-detail">
        <span class="music-detail-name">{{ getMusicResourceDisplayName(currentAudio) }}</span>
        <span class="music-detail-time">{{ playbackTimeLabel }}</span>
      </div>
      <div class="preview-actions">
        <n-button size="small" @click="emit('toggleCollect')">
          {{ collected ? '取消收藏' : '加入收藏' }}
        </n-button>
        <n-button
          size="small"
          type="primary"
          secondary
          :loading="applying"
          :disabled="!currentAudio || alreadyApplied"
          @click="applyCurrentAudio"
        >
          {{ alreadyApplied ? '已使用' : '应用' }}
        </n-button>
      </div>
    </header>

    <div v-if="musicResources.length && !previewLoading" class="music-list">
      <button
        v-for="item in musicResources"
        :key="item.id"
        type="button"
        class="music-item"
        :class="{ 'music-item-active': selectedAudioId === item.id }"
        @click="selectedAudioId = item.id"
      >
        <span class="music-main">
          <span class="music-name">{{ getMusicResourceDisplayName(item) }}</span>
          <span class="music-duration">{{ formatMaterialMusicDuration(resolveResourceDurationSeconds(item)) }}</span>
        </span>
        <span class="music-actions">
          <n-button size="tiny" @click.stop="togglePlay(item)">
            {{ playingAudioId === item.id && isPlaying ? '暂停' : '播放' }}
          </n-button>
        </span>
      </button>
    </div>
    <n-empty v-else-if="previewLoading" description="正在加载预览…" class="preview-empty" />
    <n-empty v-else description="当前素材没有可试听资源" class="preview-empty" />
  </div>
</template>

<style scoped>
.preview-pane {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 100%;
  overflow: visible;
  padding: 8px;
  box-sizing: border-box;
  background: var(--theme-bg-panel);
  font-family: var(--material-font-family, "Microsoft YaHei", "PingFang SC", Arial, sans-serif);
  font-size: var(--material-font-size-base, 12px);
  line-height: var(--material-line-height, 1.35);
  color: var(--theme-text-primary);
}

.preview-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.preview-title {
  font-size: var(--material-font-size-title, 13px);
  font-weight: 600;
  color: var(--theme-text-heading);
}

.preview-subtitle {
  font-size: var(--material-font-size-small, 11px);
  color: var(--theme-text-secondary);
  line-height: var(--material-line-height, 1.35);
  word-break: break-word;
}

.music-detail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
  border-top: 1px solid var(--theme-border);
}

.music-detail-name {
  min-width: 0;
  flex: 1;
  font-size: var(--material-font-size-base, 12px);
  color: var(--theme-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.music-detail-time {
  flex-shrink: 0;
  font-size: var(--material-font-size-small, 11px);
  color: var(--theme-text-secondary);
  font-variant-numeric: tabular-nums;
}

.preview-actions {
  display: flex;
  gap: 6px;
}

.music-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: visible;
}

.music-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-primary);
  text-align: left;
  cursor: pointer;
}

.music-item-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.music-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.music-name {
  font-size: var(--material-font-size-base, 12px);
  color: var(--theme-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.music-duration {
  flex-shrink: 0;
  font-size: var(--material-font-size-small, 11px);
  color: var(--theme-text-secondary);
  font-variant-numeric: tabular-nums;
}

.music-actions {
  flex-shrink: 0;
}

.preview-empty {
  flex: 1;
}
</style>
